// Build a pipe system from the pipe ends (owner, 2026-10-19): "terminate them with a simple right click on the end
// to then select the fitting to terminate the end of the pipe, and then let me extend the other ends of the fitting
// pipes and continue".
//
// A right-click on a FREE pipe end lists the fittings that fit it: 90° and 45° bends (turn left / turn right), tees
// (branch left / branch right, equal or with a smaller branch) and reducers (to a smaller or a larger size). The chosen
// fitting is made at the pipe's size, turned so that one of its ends sits on the pipe end facing back along the pipe,
// and that end is attached to the pipe. The fitting end that carries the run on is returned, so the Pipe tool can
// start from it at once.
//
// LEFT and RIGHT are as seen when you travel along the pipe toward its end, on the screen (Y down).
// Pure: no store, no UI. tests/pipeTerminate.test.ts covers it.
import type { Vec } from "../core/geometry";
import type { Doc, Entity } from "../doc/entities";
import { applyCommand, type Command } from "../doc/commands";
import { makeFittingItem, PIPE_SIZES, type FittingKind } from "../library/fittings";
import type { LibraryItem } from "../library/library";
import { pasteCommand } from "../tools/editOps";
import { rotatePatches } from "../tools/entityOps";
import { pipeMetaOf, setPipePatches, type FittingPortData } from "./api";
import { checkValveItemFor, checkValveRowFor } from "../library/checkValves";
import { butterflyItemFor, butterflyRowFor, hasOperator, type ButterflyOperator } from "../library/butterflyValves";
import { partsInColour, runColourOf } from "./runColour";

export type EndGroup = "bend" | "tee" | "tee-smaller" | "reducer" | "valve" | "butterfly" | "cap";

export interface EndOption {
  /** What the menu shows, for example "Bend 90° · turn left". */
  label: string;
  group: EndGroup;
  /**
   * "checkvalve": the check valve with mounting kit of this pipe size (src/library/checkValves.ts). "butterfly" (3.25.0):
   * the ERA butterfly valve of this pipe size (src/library/butterflyValves.ts), with `operator` and `openPct`.
   */
  kind: FittingKind | "checkvalve" | "butterfly";
  /** Butterfly valve: lever or gearbox. */
  operator?: ButterflyOperator;
  /** Butterfly valve: 100 = open, 0 = closed. */
  openPct?: number;
  /** Run size (bend, tee) or the large end (reducer), mm. */
  size: number;
  /** Branch (tee) or small end (reducer), mm. */
  size2?: number;
  /** The fitting end that is attached to the pipe end. */
  attach: number;
  /** The fitting end the Pipe tool starts from after the fitting is placed (null: none). */
  cont: number | null;
}

/** The end of a pipe that a fitting goes on: where it is, the way out of the pipe, and the pipe size. */
export interface PipeEnd {
  pipeId: string;
  /** 0 = end A, 1 = end B */
  index: 0 | 1;
  p: Vec;
  /** unit vector pointing OUT of the pipe */
  dir: Vec;
  size: number;
}

const ang = (v: Vec): number => Math.atan2(v.y, v.x);
const rot = (v: Vec, a: number): Vec => ({ x: v.x * Math.cos(a) - v.y * Math.sin(a), y: v.x * Math.sin(a) + v.y * Math.cos(a) });

/** "left" or "right" of the travel direction u, for a direction d leaving the fitting (screen, Y down). */
export function sideOf(u: Vec, d: Vec): "left" | "right" {
  return d.x * u.y - d.y * u.x > 0 ? "left" : "right";
}

function portsOfItem(item: LibraryItem): FittingPortData[] {
  const g = item.entities.find((e) => e.type === "group" && Array.isArray(e.meta?.ports)) as Entity | undefined;
  return (g?.meta?.ports as FittingPortData[] | undefined) ?? [];
}

/** The turn (radians, clockwise on the screen) that brings the fitting end `attach` onto a pipe end leaving along u. */
function turnFor(item: LibraryItem, attach: number, u: Vec): number {
  const q = portsOfItem(item)[attach]!;
  return ang({ x: -u.x, y: -u.y }) - ang(q.dir);
}

/** The direction fitting end `index` points to after the turn for `attach`. */
function dirAfter(item: LibraryItem, attach: number, index: number, u: Vec): Vec {
  return rot(portsOfItem(item)[index]!.dir, turnFor(item, attach, u));
}

/** The fittings that can go on a pipe end leaving along `dir`, of size `size`: bends, tees and reducers. */
export function endOptions(end: { dir: Vec; size: number }): EndOption[] {
  const u = end.dir;
  const s = end.size;
  const out: EndOption[] = [];
  const pairBy = (o: EndOption) => (o.label.includes("left") ? 0 : 1);

  for (const angle of [90, 45] as const) {
    const kind: FittingKind = angle === 90 ? "bend90" : "bend45";
    const item = makeFittingItem(kind, s);
    const two: EndOption[] = [0, 1].map((attach) => {
      const other = 1 - attach;
      const side = sideOf(u, dirAfter(item, attach, other, u));
      return { label: `Bend ${angle}° · turn ${side}`, group: "bend", kind, size: s, attach, cont: other };
    });
    out.push(...two.sort((a, b) => pairBy(a) - pairBy(b)));
  }
  // 3.22.0: a 90° bend whose outlet rises or drops: on the plan the pipe ends in the circle of the vertical leg. The
  // run does not go on along the plan.
  for (const kind of ["bendup", "benddown"] as const) {
    out.push({ label: `Bend 90° · ${kind === "bendup" ? "up (riser)" : "down (drop)"}`, group: "bend", kind, size: s, attach: 0, cont: null });
  }

  const tees = (branch: number, group: EndGroup) => {
    const item = makeFittingItem("tee", s, branch);
    const two: EndOption[] = [0, 1].map((attach) => {
      const side = sideOf(u, dirAfter(item, attach, 2, u));
      const words = branch === s ? `Tee · branch ${side}` : `Tee · branch ${branch} mm, ${side}`;
      return { label: words, group, kind: "tee" as const, size: s, size2: branch, attach, cont: 1 - attach };
    });
    return two.sort((a, b) => pairBy(a) - pairBy(b));
  };
  // the branch up (a riser) or down (a drop): the run carries on (3.15.1)
  const vertical = (branch: number, group: EndGroup): EndOption[] =>
    (["teeup", "teedown"] as const).map((kind) => ({
      label: branch === s ? `Tee · branch ${kind === "teeup" ? "up (riser)" : "down (drop)"}` : `Tee · branch ${branch} mm, ${kind === "teeup" ? "up" : "down"}`,
      group,
      kind,
      size: s,
      size2: branch,
      attach: 0,
      cont: 1
    }));
  out.push(...tees(s, "tee"), ...vertical(s, "tee"));
  for (const b of [...PIPE_SIZES].filter((x) => x < s).sort((a, c) => c - a)) out.push(...tees(b, "tee-smaller"), ...vertical(b, "tee-smaller"));

  for (const d of [...PIPE_SIZES].filter((x) => x < s).sort((a, c) => c - a)) {
    out.push({ label: `Reducer · to ${d} mm`, group: "reducer", kind: "reducer", size: s, size2: d, attach: 0, cont: 1 });
  }
  // a check valve with mounting kit, when the catalogue has this size (DN80 to DN300): the flow on from this pipe
  // (its inlet on the pipe end) or into this pipe (its outlet on the pipe end)
  if (checkValveRowFor(s)) {
    out.push({ label: `Check valve · DN${s} · flow goes on from this pipe`, group: "valve", kind: "checkvalve", size: s, attach: 0, cont: 1 });
    out.push({ label: `Check valve · DN${s} · flow comes into this pipe`, group: "valve", kind: "checkvalve", size: s, attach: 1, cont: 0 });
  }
  // 3.25.0 (owner, 2026-10-23: "I cannot right click and attach a valve, either a butterfly valve or a geared valve, to
  // a pipe while drawing it"): the ERA butterfly valve of this size, with a lever or a gearbox, open or closed. Side A
  // goes on the pipe end; the run goes on from side B.
  const bf = butterflyRowFor(s);
  if (bf) {
    for (const operator of ["lever", "gearbox"] as const) {
      if (!hasOperator(bf, operator)) continue;
      for (const openPct of [100, 0]) {
        out.push({ label: `Butterfly valve · DN${s} · ${operator} · ${openPct ? "open" : "closed"}`, group: "butterfly", kind: "butterfly", size: s, operator, openPct, attach: 0, cont: 1 });
      }
    }
  }
  for (const D of [...PIPE_SIZES].filter((x) => x > s).sort((a, c) => a - c)) {
    out.push({ label: `Reducer · up to ${D} mm`, group: "reducer", kind: "reducer", size: D, size2: s, attach: 1, cont: 0 });
  }
  // 3.22.0: an end cap closes the pipe
  out.push({ label: `End cap · ${s} mm`, group: "cap", kind: "cap", size: s, attach: 0, cont: null });
  return out;
}

/**
 * ONE command that places the fitting of `opt` on the pipe end and attaches the pipe to it. Null when the end is not a
 * free end of a pipe. `fittingId` is the new fitting group; `cont` is the end to carry the run on from.
 */
export function placeFittingAtEnd(doc: Doc, layerId: string, end: PipeEnd, opt: EndOption): { cmd: Command; fittingId: string; cont: number | null } | null {
  const pm = pipeMetaOf(doc.entities[end.pipeId]);
  if (!pm) return null;
  const anchor = end.index === 0 ? pm.a : pm.b;
  if (anchor.kind !== "free") return null;
  const item =
    opt.kind === "checkvalve"
      ? checkValveItemFor(opt.size)
      : opt.kind === "butterfly"
        ? butterflyItemFor(opt.size, { operator: opt.operator ?? "lever", openPct: opt.openPct ?? 100, view: "plan" })
        : makeFittingItem(opt.kind, opt.size, opt.size2);
  if (!item) return null;
  const q = portsOfItem(item)[opt.attach];
  if (!q) return null;
  // 1. the fitting with its chosen end on the pipe end, in the colour of the pipe (3.18.0, runColour.ts)
  const parts = partsInColour(item.entities, runColourOf(doc, end.pipeId));
  const paste = pasteCommand({ entities: parts }, layerId, { x: end.p.x - q.p.x, y: end.p.y - q.p.y });
  if (!paste) return null;
  const fittingId = paste.newIds[0]!;
  const draft = structuredClone(doc);
  applyCommand(draft, paste.cmd);
  // 2. turned about that point so the end faces back along the pipe
  const deg = (turnFor(item, opt.attach, end.dir) * 180) / Math.PI;
  const turn = Math.abs(deg % 360) < 1e-9 ? [] : rotatePatches(draft, [fittingId], end.p, deg);
  if (turn.length) applyCommand(draft, { kind: "updateEntities", patches: turn });
  // 3. the pipe end attached to it (joined: no line across the end)
  const join = setPipePatches(
    draft,
    end.pipeId,
    end.index === 0 ? { a: { kind: "port", ownerId: fittingId, port: opt.attach }, endA: "none" } : { b: { kind: "port", ownerId: fittingId, port: opt.attach }, endB: "none" }
  );
  const commands: Command[] = [paste.cmd];
  if (turn.length) commands.push({ kind: "updateEntities", patches: turn });
  if (join.length) commands.push({ kind: "updateEntities", patches: join });
  return { cmd: { kind: "batch", label: "add fitting", commands }, fittingId, cont: opt.cont };
}
